"use client";

import type React from "react";

import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  type ReactNode,
} from "react";
import { userStreamBookAudio } from "../apiservice/userService";

type AudioState = "stopped" | "playing" | "paused" | "finished";

interface MusicObject {
  id: string;
  musicFile: string;
  title: string;
  artist?: string;
  description?: string;
  coverImage?: string;
  duration?: string;
}

interface AudioContextType {
  audioRef: React.RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  togglePlay: () => void;
  currentTime: number;
  duration: number;
  seekAudio: (newTime: number) => void;
  volume: number;
  setVolume: (newVolume: number) => void;
  speed: number;
  changeSpeed: () => void;
  setPlaybackRate: (rate: number) => void;
  rewindAudio: () => void;
  forwardAudio: () => void;
  isRepeat: boolean;
  toggleRepeat: () => void;
  setAudioSource: (src: string, metadata?: any) => void;
  audioState: AudioState;
  setAudioState: (state: AudioState) => void;
  musicList: MusicObject[];
  setMusicList: (list: MusicObject[]) => void;
  nextAudio: () => void;
  prevAudio: () => void;
  currentMusic: MusicObject | null;
  addToMusicList: (music: MusicObject) => void;
  currentPlayerUrl: string;
  setCurrentPlayerUrl: (url: string) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

interface AudioProviderProps {
  children: ReactNode;
}

export function AudioProvider({ children }: AudioProviderProps) {
  const [currentPlayerUrl, setCurrentPlayerUrl] = useState<string>("");
  const [musicList, setMusicList] = useState<MusicObject[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isRepeat, setIsRepeat] = useState<boolean>(false);
  const [speed, setSpeed] = useState<number>(1);
  const [volume, setVolume] = useState<number>(0.7);
  const [audioState, setAudioState] = useState<AudioState>("stopped");

  // Get current music based on the track index
  const currentMusic =
    musicList.length > 0
      ? musicList[currentTrackIndex]
      : audioRef.current.src
      ? {
          id: "current",
          musicFile: audioRef.current.src,
          title: audioRef.current.title || "Unknown Title",
          artist: "Unknown Artist",
          coverImage: "/images/homePage/placeHolder.png",
        }
      : null;

  // Update audio when track changes or music list changes
  useEffect(() => {
    if (currentMusic && musicList.length > 0) {
      if (audioRef.current) {
        setIsPlaying(false);
        audioRef.current.pause();
      }
      audioRef.current.src = currentMusic.musicFile;
      audioRef.current.load();
      setCurrentTime(0);
      setDuration(0);
      setAudioState("stopped");

      showNotification(currentMusic?.title, currentMusic?.coverImage);

      //Update the Stream Number on Server
      updateStream(currentMusic);
      // Auto-play when track changes
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Auto-play prevented:", error);
        });
      }
    }
  }, [currentTrackIndex, musicList]);

  function showNotification(title, imageUrl) {
    if ("Notification" in window) {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          new Notification(title, {
            body: "Now playing...",
            icon: imageUrl || "/default-image.png", // Update image dynamically
          });
        }
      });
    }

    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentMusic?.title || "Now Playing",
        artist: currentMusic?.artist || "Unknown Artist",
        album: "Keyway Audio Books",
        artwork: [
          {
            src: currentMusic?.coverImage || "/images/homePage/placeHolder.png",
            sizes: "512x512",
            type: "image/png",
          },
        ],
      });

      // Play Event
      navigator.mediaSession.setActionHandler("play", () => {
        console.log("Play button pressed");
        togglePlay(); // Call your app's play function
      });

      // Pause Event
      navigator.mediaSession.setActionHandler("pause", () => {
        console.log("Pause button pressed");
        togglePlay(); // Call your app's pause function
      });

      // Next Track Event
      navigator.mediaSession.setActionHandler("nexttrack", () => {
        console.log("Next track button pressed");
        nextAudio(); // Call your app's next track function
      });

      // Previous Track Event
      navigator.mediaSession.setActionHandler("previoustrack", () => {
        console.log("Previous track button pressed");
        prevAudio(); // Call your app's previous track function
      });
    }
  }

  const updateStream = async (currentMusic: MusicObject) => {
    try {
      const streamUpdateResult = await userStreamBookAudio(currentMusic?.id);
      console.log("Stream updated:", streamUpdateResult);
    } catch (error) {
      console.error("Failed to update stream:", error);
    }
  };

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      if (!isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setAudioState("playing");
    };

    const handlePause = () => {
      setIsPlaying(false);
      setAudioState("paused");
    };

    const handleEnd = () => {
      if (isRepeat) {
        audio.currentTime = 0;
        audio.play().catch((error) => {
          console.error("Error replaying audio:", error);
        });
      } else if (
        musicList.length > 0 &&
        currentTrackIndex < musicList.length - 1
      ) {
        nextAudio();
      } else {
        setAudioState("finished");
        setIsPlaying(false);
      }
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnd);

    // Set initial volume
    audio.volume = volume;

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnd);
    };
  }, [isRepeat, currentTrackIndex, musicList.length, volume]);

  // Toggle play/pause
  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Play prevented:", error);
        });
      }
    }
  };

  // Seek to a specific time
  const seekAudio = (newTime: number) => {
    if (!isNaN(newTime) && isFinite(newTime)) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Cycle through playback speeds (1 -> 1.5 -> 2 -> 1)
  const changeSpeed = () => {
    const newSpeed = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    audioRef.current.playbackRate = newSpeed;
    setSpeed(newSpeed);
  };

  // Set a specific playback rate
  const setPlaybackRate = (rate: number) => {
    if (rate >= 0.5 && rate <= 3) {
      audioRef.current.playbackRate = rate;
      setSpeed(rate);
    }
  };

  // Set volume (0-1)
  const setVolumeLevel = (newVolume: number) => {
    if (newVolume >= 0 && newVolume <= 1) {
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
    }
  };

  // Rewind 10 seconds
  const rewindAudio = () => {
    const newTime = Math.max(0, audioRef.current.currentTime - 10);
    seekAudio(newTime);
  };

  // Forward 10 seconds
  const forwardAudio = () => {
    const newTime = Math.min(duration, audioRef.current.currentTime + 10);
    seekAudio(newTime);
  };

  // Toggle repeat mode
  const toggleRepeat = () => {
    setIsRepeat(!isRepeat);
  };

  // Set a new audio source
  const setAudioSource = (src: string, metadata?: any) => {
    if (!src) return;

    if (audioRef.current) {
      setIsPlaying(false);
      audioRef.current.pause();
    }

    audioRef.current.src = src;
    audioRef.current.load();
    setCurrentTime(0);
    setDuration(0);
    setAudioState("stopped");

    // Create a music object for the current source
    const newMusic: MusicObject = {
      id: metadata?.id || "current",
      musicFile: src,
      title: metadata?.title || "Audio Book",
      artist: metadata?.artist || "Unknown Artist",
      description: metadata?.description || "",
      coverImage: metadata?.coverImage || "/images/homePage/placeHolder.png",
    };

    // Check if this track is already in the music list
    const existingIndex = musicList.findIndex((item) => item.musicFile === src);

    if (existingIndex >= 0) {
      // If it exists, update the current track index
      setCurrentTrackIndex(existingIndex);
    } else {
      // If it doesn't exist, add it to the list
      const updatedList = [...musicList, newMusic];
      setMusicList(updatedList);
      setCurrentTrackIndex(updatedList.length - 1);
    }

    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error("Error playing new audio source:", error);
      });
    }
  };

  // Play next track
  const nextAudio = () => {
    if (musicList.length > 0 && currentTrackIndex < musicList.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1);
    }
  };

  // Play previous track
  const prevAudio = () => {
    if (musicList.length > 0) {
      if (currentTime > 3) {
        // If more than 3 seconds into the track, restart it
        seekAudio(0);
      } else if (currentTrackIndex > 0) {
        // Otherwise go to previous track
        setCurrentTrackIndex(currentTrackIndex - 1);
      }
    }
  };

  // Add a new track to the music list
  const addToMusicList = (music: MusicObject) => {
    // Check if music already exists in the list
    const exists = musicList.some(
      (item) => item.id === music.id || item.musicFile === music.musicFile
    );
    if (!exists) {
      setMusicList([...musicList, music]);
    }
  };

  return (
    <AudioContext.Provider
      value={{
        audioRef,
        isPlaying,
        togglePlay,
        currentTime,
        duration,
        seekAudio,
        volume,
        setVolume: setVolumeLevel,
        speed,
        changeSpeed,
        setPlaybackRate,
        rewindAudio,
        forwardAudio,
        isRepeat,
        toggleRepeat,
        setAudioSource,
        audioState,
        setAudioState,
        musicList,
        setMusicList,
        nextAudio,
        prevAudio,
        currentMusic,
        addToMusicList,
        currentPlayerUrl,
        setCurrentPlayerUrl,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio(): AudioContextType {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within an AudioProvider");
  }
  return context;
}
